// capacitor-http.helper.ts
import { CapacitorHttp, HttpOptions } from '@capacitor/core';
import { environment } from 'src/environments/environment';
import { EncryptionHelper } from './encryption.helper';
import { LanguageService } from '../services/language.service';
import { SpinnerService } from '../services/spinner.service';
import { inject } from '@angular/core';
import { AccountService } from '../services/account.service';

export class CapacitorHttpHelper {

  private static spinnerService: SpinnerService | null = null;
  private static accountService: AccountService | null = null;
  /**
   * Inicializa el SpinnerService para uso en métodos estáticos
   */
  static initSpinnerService(spinnerService: SpinnerService): void {
    this.spinnerService = spinnerService;
  }


  /**
   * Realiza una petición POST con CapacitorHttp añadiendo automáticamente ApiKey e idUser si hay sesión válida
   */
  static async post(options: HttpOptions): Promise<any> {
    // Mostrar spinner
    this.spinnerService?.show();

    try {
      // Verificar sesión y añadir datos automáticamente
      const modifiedOptions = this.addSessionDataToOptions(options);
      const result = await CapacitorHttp.post(modifiedOptions);
      return result;
    } catch (error) {
      throw error;
    } finally {
      // Ocultar spinner
      this.spinnerService?.hide();
    }
  }

  /**
   * Realiza una petición GET con CapacitorHttp añadiendo automáticamente ApiKey e idUser si hay sesión válida
   */
  static async get(options: HttpOptions): Promise<any> {
    // Mostrar spinner
    this.spinnerService?.show();

    try {
      // Verificar sesión y añadir datos automáticamente
      const modifiedOptions = this.addSessionDataToOptions(options);
      const result = await CapacitorHttp.get(modifiedOptions);
      return result;
    } catch (error) {
      throw error;
    } finally {
      // Ocultar spinner
      this.spinnerService?.hide();
    }
  }

  /**
   * Realiza una petición POST silenciosa (sin spinner) con CapacitorHttp
   * Útil para recargas automáticas en segundo plano
   */
  static async postSilent(options: HttpOptions): Promise<any> {
    try {
      // Verificar sesión y añadir datos automáticamente
      const modifiedOptions = this.addSessionDataToOptions(options);
      const result = await CapacitorHttp.post(modifiedOptions);
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Realiza una petición GET silenciosa (sin spinner) con CapacitorHttp
   * Útil para recargas automáticas en segundo plano
   */
  static async getSilent(options: HttpOptions): Promise<any> {
    try {
      // Verificar sesión y añadir datos automáticamente
      const modifiedOptions = this.addSessionDataToOptions(options);
      const result = await CapacitorHttp.get(modifiedOptions);
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Realiza una petición PUT con CapacitorHttp añadiendo automáticamente ApiKey e idUser si hay sesión válida
   */
  static async put(options: HttpOptions): Promise<any> {
    // Mostrar spinner
    this.spinnerService?.show();

    try {
      // Verificar sesión y añadir datos automáticamente
      const modifiedOptions = this.addSessionDataToOptions(options);
      const result = await CapacitorHttp.put(modifiedOptions);
      return result;
    } catch (error) {
      throw error;
    } finally {
      // Ocultar spinner
      this.spinnerService?.hide();
    }
  }

  /**
   * Realiza una petición DELETE con CapacitorHttp añadiendo automáticamente ApiKey e idUser si hay sesión válida
   */
  static async delete(options: HttpOptions): Promise<any> {
    // Mostrar spinner
    this.spinnerService?.show();

    try {
      // Verificar sesión y añadir datos automáticamente
      const modifiedOptions = this.addSessionDataToOptions(options);
      const result = await CapacitorHttp.delete(modifiedOptions);
      return result;
    } catch (error) {
      throw error;
    } finally {
      // Ocultar spinner
      this.spinnerService?.hide();
    }
  }

  /**
   * Realiza una petición POST con archivos usando FormData
   * @param url URL del endpoint
   * @param formData FormData con los archivos y datos
   * @returns Promise con la respuesta
   */
  static async postWithFiles(url: string, formData: FormData): Promise<any> {
    // Mostrar spinner
    this.spinnerService?.show();

    try {
      const session = this.getValidSession();

      // Agregar datos de sesión al FormData
      if (session) {
        formData.append('ApiKey', environment.apiKey);
        formData.append('idUser', session.idUser.toString());
      }
      formData.append('lang', LanguageService.getCurrentLanguage());

      // Usar fetch nativo para enviar FormData (funciona mejor con archivos)
      const response = await fetch(url, {
        method: 'POST',
        body: formData,
        // No establecer Content-Type, el navegador lo hace automáticamente con boundary
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      throw error;
    } finally {
      // Ocultar spinner
      this.spinnerService?.hide();
    }
  }

  /**
   * Añade automáticamente ApiKey e idUser a las opciones si hay sesión válida
   */
  private static addSessionDataToOptions(options: HttpOptions): HttpOptions {
    const session = this.getValidSession();
    const token = localStorage.getItem('auth_token');
    const currentLanguage = LanguageService.getCurrentLanguage();
    
    console.log('Valid session in CapacitorHttp request:', session);
    console.log('Token in CapacitorHttp request:', token);
    
    const modifiedOptions = { ...options };

    // Agregar Accept-Language a las cabeceras
    modifiedOptions.headers = {
      ...modifiedOptions.headers,
      'Accept-Language': currentLanguage
    };
    
    if (!session) {
      return modifiedOptions;
    }

    // Para POST, PUT, PATCH - añadir al body
    if (modifiedOptions.data && typeof modifiedOptions.data === 'object') {
      modifiedOptions.data = {
        ...modifiedOptions.data,
        lang: currentLanguage,
        token: token,
        idUser: session.idUser
      };
    }

    return modifiedOptions;
  }

  /**
   * Obtiene una sesión válida (no expirada y con integridad correcta)
   * Retorna null si no hay sesión, está expirada o no es íntegra
   */
  private static getValidSession(): any {
    try {
      // Verificar si hay sesión
      const sessionData = localStorage.getItem('session');
      if (!sessionData) return null;

      // Verificar si ha expirado
      if (EncryptionHelper.isSessionExpired()) {
        console.warn('Session expired in CapacitorHttp request, clearing sessions');
        EncryptionHelper.clearSessions();
        return null;
      }

      // Verificar integridad
      if (!EncryptionHelper.verifySessionIntegrity()) {
        console.warn('Session integrity failed in CapacitorHttp request, clearing sessions');
        EncryptionHelper.clearSessions();
        return null;
      }

      return JSON.parse(sessionData);
    } catch (error) {
      console.error('Error validating session in CapacitorHttp:', error);
      EncryptionHelper.clearSessions();
      return null;
    }
  }
}
